import React, { FC, type JSX } from 'react'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2, Heading4 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useWindowResize } from '@utils/domUtils'
import { placeholder } from '@utils/placeholder'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from './TableSummary.module.scss'

export interface SummaryItem {
  title: string
  value: string | number | JSX.Element
  infoClick?: () => void
  infoTooltip?: string
  condition?: boolean
}

interface Props {
  summaryItems: SummaryItem[]
  isLoading?: boolean
}

export const TableSummary: FC<Props> = ({ summaryItems, isLoading }) => {
  const isMobile = useWindowResize()
  const infoModal = useModal(InformationModal)

  const visibleItems = summaryItems.filter(({ condition }) => condition ?? true)
  const count = visibleItems.length

  const justify = count === 1 ? 'flex-start' : count === 2 ? 'space-around' : 'space-between'

  return (
    <Box flex wrap gap='x16' justify={justify} fullWidth mt='x16' py='x16'>
      <Loading wrapperClass={styles.loading} isLoading={!!isLoading} showLoadingIcon>
        {visibleItems.map(({ title, value, infoClick, infoTooltip }, index) => {
          const justify =
            index === 0 ? 'flex-start' : index === visibleItems.length - 1 ? 'flex-end' : 'center'

          return (
            <Box
              key={title}
              flex
              direction='column'
              align='center'
              gap='x4'
              flexItem={isMobile ? '1 1 calc(50% - 8px)' : undefined}
            >
              <Box align='center'>
                <Heading4 medium>
                  <SensitiveData>
                    {typeof value === 'string' || typeof value === 'number'
                      ? placeholder.dash(value)
                      : value}
                  </SensitiveData>
                </Heading4>
              </Box>
              <Box fullWidth flex justify={isMobile ? 'center' : justify} gap='x4'>
                <Body2>{title}</Body2>
                {infoClick && <ActionIcon icon={InfoIcon} size='x20' onClick={infoClick} />}
                {infoTooltip && !isMobile && (
                  <Tooltip content={infoTooltip}>
                    <ActionIcon icon={InfoIcon} size='x20' onClick={infoClick} />
                  </Tooltip>
                )}
                {infoTooltip && isMobile && (
                  <ActionIcon
                    icon={InfoIcon}
                    size='x20'
                    onClick={() => infoModal.open({ description: infoTooltip })}
                  />
                )}
              </Box>
            </Box>
          )
        })}
      </Loading>
    </Box>
  )
}
